import styled from 'styled-components';
import { BODY_MAX_WIDTH } from '../constants';
import CloseModal from '../assets/icons/closeModal.svg?react';
import { Typography } from './Typography/Typography';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { focusVisibleStyles, standardTransitionStyles } from '../sharedStyles';
import ButtonRow, { Alignment } from './ButtonRow/ButtonRow';
import Button from './Button/Button';
import { useState } from 'react';

export { Alignment as ModalFooterAlignment };

export const enum BorderRadiusSize {
  sm = 'var(--border-radius-small)',
  md = 'var(--border-radius-medium)',
  lg = 'var(--border-radius-large)',
}

export const MODAL_DEFAULT_BORDER_RADIUS_SIZE = BorderRadiusSize.sm;
export const MODAL_OUTER_PADDING = '20px';
export const MODAL_INNER_GAP = '16px';

const ModalBackdrop = styled(DialogBackdrop)<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  // TODO(tokens): Replace legacy color token --gray-overlay-backdrop with numbered equivalent once available.
  background-color: var(--gray-overlay-backdrop);
  z-index: 1000;
`;

const ContentContainer = styled(DialogPanel)<{
  $open: boolean;
  $fullHeight: boolean;
  $wide: boolean;
  $maxWidth?: string;
  $borderRadius?: string;
}>`
  position: fixed;
  z-index: 1000;
  background-color: var(--white);
  height: ${({ $fullHeight }) => ($fullHeight ? '70%' : 'auto')};
  display: flex;
  flex-direction: column;

  @media (max-width: ${BODY_MAX_WIDTH}) {
    // TODO: check with design if this is still the case
    // on mobile, we want the modal to be a full width bottom sheet
    bottom: 0;
    left: 0;
    right: 0;
    max-height: ${({ $open }) => ($open ? '100vh' : '0')};
    max-height: ${({ $open }) => ($open ? '100dvh' : '0')}; // iOS safari
    transition: max-height 0.3s ease-out;
    border-radius: ${({ $borderRadius }) => `${$borderRadius} ${$borderRadius}`} 0 0;
  }

  @media (min-width: ${BODY_MAX_WIDTH}) {
    // on desktop, we want the modal to be a fixed width centered on the page
    top: 64px;
    left: 50%;
    transform: translateX(-50%);
    max-height: calc(100vh - 64px);
    max-width: ${({ $wide, $maxWidth }) => ($wide ? '60%' : $maxWidth || BODY_MAX_WIDTH)};
    width: 100%;
    border-radius: ${({ $borderRadius }) => $borderRadius};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  width: 48px;
  height: 48px;
  text-align: center;
  color: var(--grayscale-900);
  ${standardTransitionStyles}
  // for the focus outline
  border-radius: var(--border-radius-medium);

  ${focusVisibleStyles}
`;

export const ModalTitle = styled(Typography).attrs(({ styledAs, renderedAs }) => ({
  styledAs: styledAs || 'h5',
  renderedAs: renderedAs || 'h1',
}))`
  // no padding on the bottom to avoid double spacing against the ModalBody
  padding: ${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING} 0;
`;

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${MODAL_INNER_GAP};
  overflow-y: auto;
  flex-grow: 1;
  // top and bottom padding uses inner gap to separate from the ModalTitle and ModalFooter
  // left and right padding uses outer padding because there's nothing to the left or right of the ModalBody
  padding: ${MODAL_INNER_GAP} ${MODAL_OUTER_PADDING};
`;

export const ModalFooter = styled(ButtonRow)`
  // no padding on the top to avoid double spacing against the ModalBody
  padding: 0 ${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING};
`;

interface ModalProps {
  children: React.ReactNode;
  // Optional prop for telling the modal if it's open or not.
  // Alternatively, you can conditionally render the Modal without `open`. Example:
  // {modalIsOpen && (<Modal>...</Modal>)}
  open?: boolean;
  onClose: () => void;
  hideCloseButton?: boolean;
  fullHeight?: boolean;
  wide?: boolean;
  /**
   * The max width of the modal. Is not applied if `wide` is true.
   */
  maxWidth?: string;
  borderRadiusSize?: BorderRadiusSize;
  /**
   * If true, a confirm dialog will be shown when the user tries to close the modal.
   */
  hasUnsavedChanges?: boolean;
  footerButtonConfig?: ModalFooterButtonConfig;
}

interface SubmitButtonProps {
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}

interface ModalFooterButtonConfig {
  // Calls onClose or shows the confirm dialog if hasUnsavedChanges is true
  showCancelButton?: boolean;
  // Calls onSubmit
  submitButton?: SubmitButtonProps;
}

const ConfirmDialogContents = ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => {
  return (
    <>
      <ModalTitle>Discard changes?</ModalTitle>
      <ModalBody>
        <Typography>You have unsaved changes. Are you sure you want to close without saving?</Typography>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onCancel}>
          Keep editing
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Discard changes
        </Button>
      </ModalFooter>
    </>
  );
};

const ModalFooterButtons = ({
  onCancel,
  footerButtonConfig,
}: {
  onCancel: () => void;
  footerButtonConfig: ModalFooterButtonConfig;
}) => {
  return (
    <ModalFooter>
      {footerButtonConfig.showCancelButton && (
        <Button variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
      )}
      {footerButtonConfig.submitButton && (
        <Button
          variant="primary"
          onClick={footerButtonConfig.submitButton.onClick}
          disabled={footerButtonConfig.submitButton.disabled}
          isLoading={footerButtonConfig.submitButton.isLoading}
        >
          {footerButtonConfig.submitButton.label}
        </Button>
      )}
    </ModalFooter>
  );
};

/**
 * Displays content in an overlay on top of the page.
 *
 * For the title at the top of the modal content, you can use the ModalTitle component from Modal.tsx
 */
export const Modal = ({
  children,
  open = true,
  onClose,
  fullHeight = false,
  wide = false,
  hideCloseButton = false,
  maxWidth,
  borderRadiusSize = MODAL_DEFAULT_BORDER_RADIUS_SIZE,
  hasUnsavedChanges = false,
  footerButtonConfig,
}: ModalProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  if (showConfirmDialog) {
    return (
      <Dialog open={open} onClose={handleCloseAttempt}>
        <ModalBackdrop $open={open} />
        <ContentContainer $open={open} $fullHeight={false} $wide={false} $borderRadius={borderRadiusSize}>
          <ConfirmDialogContents onCancel={() => setShowConfirmDialog(false)} onConfirm={handleConfirmClose} />
        </ContentContainer>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleCloseAttempt}>
      <ModalBackdrop $open={open} />
      <ContentContainer
        $open={open}
        $fullHeight={fullHeight}
        $wide={wide}
        $maxWidth={maxWidth}
        $borderRadius={borderRadiusSize}
      >
        {!hideCloseButton && (
          <CloseButton onClick={handleCloseAttempt}>
            <CloseModal title="close" />
          </CloseButton>
        )}
        {children}
        {footerButtonConfig && (
          <ModalFooterButtons onCancel={handleCloseAttempt} footerButtonConfig={footerButtonConfig} />
        )}
      </ContentContainer>
    </Dialog>
  );
};
